import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import HeaderPage from "../../components/HeaderPage";
import SideBarCourseContent from "../../components/SideBarCourseContent";
import StartCourseExamCard from "../../components/cards/Exam/StartCourseExamCard";
import {
  useGetCourseQuery,
  useCreateLessonLogMutation,
  useLazyGetLessonQuery,
} from "../../services/users/courses";
import AboveCourseExamCard from "../../components/cards/Exam/AboveCourseExamCard";
import CourseExamContent from "../../components/cards/Exam/CourseExamContent";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyState from "../../components/EmptyState";
import { useTranslation } from "react-i18next";
import CourseVideoPlayer from "../../components/VideoPlayer/CourseVideoPlayer";
import AiLessonPanel from "../../components/AiCompanion/AiLessonPanel";
import { getAiFeatureFlags } from "../../utils/aiFeatureFlags";

const CoursePage = () => {
  const [currentLesson, setCurrentLesson] = useState();
  const [isAboveVideoExam, setIsAboveVideoExam] = useState(false);
  const [isExamStart, setIsExamStart] = useState(false);
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentInVideoQuestion, setCurrentInVideoQuestion] = useState();
  const [subtitleSegments, setSubtitleSegments] = useState(null);

  const [currentLogs, setCurrentLogs] = useState();
  const lessonLogs = useRef();
  const inVideoQuestions = useRef();
  const playedSecondsRef = useRef(0);

  const { t } = useTranslation(["common", "learningPaths"]);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { aiCompanionEnabled } = getAiFeatureFlags(currentUser?.organization);

  let { id } = useParams();
  const location = useLocation();
  const {
    data: courseDetails,
    isLoading: isCourseLoading,
    refetch,
  } = useGetCourseQuery(id);
  const [getLesson] = useLazyGetLessonQuery();
  const [createLessonLog] = useCreateLessonLogMutation();

  const handlePlayedSeconds = useCallback((seconds) => {
    playedSecondsRef.current = seconds;
  }, []);

  const getPlayedSeconds = useCallback(() => playedSecondsRef.current, []);

  useEffect(() => {
    setSubtitleSegments(null);
    playedSecondsRef.current = 0;
  }, [currentLesson?.id]);

  useEffect(() => {
    if (currentLesson?.questions?.length > 0) {
      inVideoQuestions.current = currentLesson?.questions?.map((question) => {
        let timeSplitted = question.time
          .split(":")
          .map((s) => parseInt(s, 10) || 0);
        let timeInSeconds;
        if (timeSplitted.length >= 3) {
          timeInSeconds =
            timeSplitted[0] * 3600 + timeSplitted[1] * 60 + timeSplitted[2];
        } else {
          timeInSeconds = timeSplitted[0] * 60 + (timeSplitted[1] ?? 0);
        }
        return {
          id: question.id,
          archived: question.archived,
          question: question.question,
          answers: question.answers,
          correct_answer: question.correct_answer,
          time: question.time,
          timeInSeconds,
        };
      });
    }
  }, [currentLesson]);

  useEffect(() => {
    if (courseDetails) {
      if (!currentLesson)
        setCurrentLesson(courseDetails?.data?.sections[0]?.lessons[0]);
      lessonLogs.current = courseDetails?.data?.lesson_logs;
      setCurrentLogs(courseDetails?.data?.lesson_logs);
    }
  }, [courseDetails]);

  const handleStartingExam = async () => {
    let lesson = await getLesson(currentLesson?.id);
    if (!lesson?.error) {
      setExamQuestions(lesson?.data?.data?.questions);
      setIsExamStart(true);
    }
  };

  if (isCourseLoading) return <LoadingIndicator />;
  if (!courseDetails?.data?.sections?.length)
    return (
      <div className="employee-course-page employee-course-page--empty">
        <EmptyState
          iconComponent={<i className="far fa-play-circle" />}
          title={t("emptyStates.videos")}
          emptyStateText={t("learningPaths:emptyVideoCourse")}
        />
      </div>
    );

  const breadcrumbItems = [
    {
      name: t("learningPathsTitle"),
      href: "/employee/learning_paths",
    },
    location.state?.learningPathName && location.state?.learningPathId
      ? {
          name: location.state.learningPathName,
          href: `/employee/learning_paths/${location.state.learningPathId}`,
        }
      : null,
    {
      name: courseDetails?.data?.title,
      href: "#",
    },
  ].filter(Boolean);

  return (
    <div className="employee-course-page">
      <HeaderPage
        progressBarFullDetails={t("learningPaths:completedFromCourse")}
        progressBarPercentageText={`${courseDetails?.data?.enrollment?.progress}% `}
        sectionTitle={courseDetails?.data?.title}
        listItems={breadcrumbItems}
      />
      <div className="container-fluid">
        <div className="row course-page employee-course-page__layout">
          <SideBarCourseContent
            lessonLogs={currentLogs}
            sections={courseDetails?.data?.sections}
            currentLesson={currentLesson}
            setCurrentLesson={setCurrentLesson}
            createLessonLog={createLessonLog}
          />

          <div className="col-lg-8 col-md-12 content-coure-page employee-course-page__content p-4">
            {currentLesson && currentLesson?.type != "Quiz" && (
              <h1 className="font_20 font-extrabold employee-course-page__lesson-title">
                {currentLesson?.name}
              </h1>
            )}

            {currentLesson?.type == "Video" && (
              <>
                <div
                  className={`video-main-container ${
                    isAboveVideoExam ? "open-exam-card" : ""
                  }`}
                >
                  {currentLesson?.video_source?.[0]?.src ||
                  currentLesson?.video_id ? (
                    <CourseVideoPlayer
                      currentLesson={currentLesson}
                      sections={courseDetails?.data?.sections || []}
                      createLessonLog={createLessonLog}
                      lessonLogsRef={lessonLogs}
                      inVideoQuestionsRef={inVideoQuestions}
                      setIsAboveVideoExam={setIsAboveVideoExam}
                      setCurrentInVideoQuestion={setCurrentInVideoQuestion}
                      onLessonChange={setCurrentLesson}
                      onPlayedSeconds={handlePlayedSeconds}
                      subtitleSegments={subtitleSegments}
                    />
                  ) : (
                    <div className="text-center p-4 employee-course-page__video-fallback">
                      <p className="mb-0">
                        {t("learningPaths:videoUnavailable")}
                      </p>
                    </div>
                  )}
                  {isAboveVideoExam && (
                    <AboveCourseExamCard
                      currentInVideoQuestion={currentInVideoQuestion}
                      setIsAboveVideoExam={setIsAboveVideoExam}
                      createLessonLog={createLessonLog}
                      currentLessonId={currentLesson.id}
                      lessonLogs={lessonLogs.current.find(
                        (log) => log.lesson_id == currentLesson.id,
                      )}
                    />
                  )}
                </div>
                {currentLesson?.id && aiCompanionEnabled && (
                  <AiLessonPanel
                    lessonId={currentLesson.id}
                    getPlayedSeconds={getPlayedSeconds}
                    onSubtitleSegmentsChange={setSubtitleSegments}
                  />
                )}
              </>
            )}

            {currentLesson?.type == "ExplanationFile" && (
              <div className="employee-course-page__file-wrap">
                <embed
                  className="employee-course-page__file"
                  src={currentLesson?.file_url}
                  type="application/pdf"
                />
              </div>
            )}

            {currentLesson?.type == "Quiz" &&
              (isExamStart ? (
                <CourseExamContent
                  questions={examQuestions}
                  createLessonLog={createLessonLog}
                  currentLessonId={currentLesson?.id}
                  refetch={refetch}
                  setCurrentLesson={setCurrentLesson}
                  getLesson={getLesson}
                  quizTimeMinutes={
                    currentLesson?.quiz_time != null &&
                    currentLesson?.quiz_time !== ""
                      ? Number(currentLesson.quiz_time)
                      : null
                  }
                />
              ) : (
                <StartCourseExamCard
                  duration={currentLesson?.quiz_time}
                  numberOfQuestion={currentLesson?.questions_count}
                  handleStartingExam={handleStartingExam}
                  examTitle={currentLesson?.name}
                  currentLessonLog={courseDetails?.data?.lesson_logs?.find(
                    (lesson_log) => lesson_log?.lesson_id == currentLesson?.id,
                  )}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
